/**
 * Exposure Alert Categories - Security service categories for intent-driven selection.
 *
 * Categories group related ports/protocols by security intent:
 * - User selects categories like "Remote Access" or "Databases"
 * - Frontend maps categories to individual port/protocol conditions
 * - Backend stores individual conditions with OR logic
 *
 * Used in:
 * - CategoryExposureAlertsForm
 * - ExposureAlertsCard (category summary display)
 */

/**
 * Risk level for exposure categories.
 * Used for visual styling and priority sorting.
 */
export type ExposureCategoryRisk = 'critical' | 'high' | 'medium';

/**
 * Category definition for exposure alerts.
 * Groups related ports/protocols by security intent.
 */
export interface ExposureCategory {
  /** Unique identifier for the category */
  id: string;
  /** Display name for the category */
  name: string;
  /** Brief description listing example services */
  description: string;
  /** Ports associated with this category */
  ports: number[];
  /** Protocols associated with this category */
  protocols: string[];
  /** Risk level for visual styling */
  risk: ExposureCategoryRisk;
  /** Whether category is recommended for most users */
  recommended: boolean;
}

/**
 * Security categories for exposure alert selection.
 * Ordered by risk level (critical first) and recommendation status.
 */
export const EXPOSURE_CATEGORIES: ExposureCategory[] = [
  {
    id: 'remote-access',
    name: 'Remote Access Services',
    description: 'SSH, RDP, VNC, Telnet',
    ports: [22, 3389, 5900, 23],
    protocols: ['ssh', 'rdp', 'vnc', 'telnet'],
    risk: 'critical',
    recommended: true,
  },
  {
    id: 'databases',
    name: 'Databases Exposed to Internet',
    description: 'PostgreSQL, MySQL, MongoDB, Redis, MSSQL, Oracle',
    ports: [5432, 3306, 27017, 6379, 1433, 1521],
    protocols: [
      'postgresql',
      'mysql',
      'mongodb',
      'redis',
      'mssql',
      'oracle-tns',
    ],
    risk: 'critical',
    recommended: true,
  },
  {
    id: 'file-sharing',
    name: 'File Sharing & SMB Services',
    description: 'SMB, NetBIOS, FTP, NFS, TFTP',
    ports: [445, 137, 138, 139, 21, 2049, 69],
    protocols: ['smb', 'ftp', 'nfs', 'tftp'],
    risk: 'high',
    recommended: true,
  },
  {
    id: 'web-logins',
    name: 'Web Login Portals',
    description: 'Admin panels, login forms, dashboards',
    ports: [80, 443, 8080, 8443],
    protocols: ['http', 'https'],
    risk: 'high',
    recommended: true,
  },
  {
    id: 'developer-ci',
    name: 'Developer & CI/CD Services',
    description: 'Jenkins, GitLab, Kubernetes, Docker',
    ports: [8080, 8081, 8082, 6443, 10250, 2375, 2376],
    protocols: ['jenkins', 'gitlab', 'kubernetes'],
    risk: 'high',
    recommended: false,
  },
  {
    id: 'email',
    name: 'Email Services',
    description: 'SMTP, IMAP, POP3',
    ports: [25, 143, 993, 110, 995, 587],
    protocols: ['smtp', 'imap', 'imaps', 'pop3'],
    risk: 'medium',
    recommended: false,
  },
  {
    id: 'directory',
    name: 'Directory Services',
    description: 'LDAP, Active Directory, Kerberos',
    ports: [389, 636, 88, 3268, 3269],
    protocols: ['ldap', 'ldaps', 'kerberos'],
    risk: 'high',
    recommended: true,
  },
  {
    id: 'management',
    name: 'Network Management Protocols',
    description: 'SNMP, WMI, IPMI',
    ports: [161, 162, 623],
    protocols: ['snmp', 'wmi', 'ipmi'],
    risk: 'high',
    recommended: true,
  },
  {
    id: 'industrial',
    name: 'Industrial Control Systems',
    description: 'Modbus TCP, OPC UA, BACnet, IEC',
    ports: [502, 4840, 47808, 2404],
    protocols: ['modbus', 'opcua', 'bacnet'],
    risk: 'critical',
    recommended: false,
  },
  {
    id: 'messaging',
    name: 'Messaging & Cache Services',
    description: 'RabbitMQ, Memcached, MQTT',
    ports: [5672, 11211, 1883, 8883],
    protocols: ['rabbitmq', 'memcached', 'mqtt'],
    risk: 'medium',
    recommended: false,
  },
];

/**
 * Gets all ports from all categories (deduplicated).
 */
export function getAllCategoryPorts(): Set<number> {
  const allPorts = new Set<number>();
  EXPOSURE_CATEGORIES.forEach(category => {
    category.ports.forEach(port => allPorts.add(port));
  });
  return allPorts;
}

/**
 * Gets all protocols from all categories (deduplicated).
 */
export function getAllCategoryProtocols(): Set<string> {
  const allProtocols = new Set<string>();
  EXPOSURE_CATEGORIES.forEach(category => {
    category.protocols.forEach(protocol => allProtocols.add(protocol));
  });
  return allProtocols;
}

/**
 * Derives selected category IDs from a set of active ports.
 * A category is "selected" if ALL its ports are active.
 *
 * @param activePorts - Set of currently active port numbers
 * @returns Array of category IDs that are fully selected
 */
export function deriveSelectedCategories(activePorts: Set<number>): string[] {
  return EXPOSURE_CATEGORIES.filter(category =>
    category.ports.every(port => activePorts.has(port))
  ).map(category => category.id);
}

/**
 * Gets ports that are not covered by any category.
 *
 * @param activePorts - Set of currently active port numbers
 * @returns Array of custom port numbers
 */
export function getCustomPorts(activePorts: Set<number>): number[] {
  const categoryPorts = getAllCategoryPorts();
  return Array.from(activePorts).filter(port => !categoryPorts.has(port));
}

/**
 * Gets protocols that are not covered by any category.
 *
 * @param activeProtocols - Set of currently active protocol names
 * @returns Array of custom protocol names
 */
export function getCustomProtocols(activeProtocols: Set<string>): string[] {
  const categoryProtocols = getAllCategoryProtocols();
  return Array.from(activeProtocols).filter(
    protocol => !categoryProtocols.has(protocol)
  );
}

/**
 * Calculates ports to add/remove when toggling a category.
 *
 * @param categoryId - The category being toggled
 * @param isSelected - Whether the category is being selected (true) or deselected (false)
 * @param currentPorts - Current set of active ports
 * @returns Object with portsToAdd and portsToRemove arrays
 */
export function calculateCategoryDiff(
  categoryId: string,
  isSelected: boolean,
  currentPorts: Set<number>
): { portsToAdd: number[]; portsToRemove: number[] } {
  const category = EXPOSURE_CATEGORIES.find(c => c.id === categoryId);
  if (!category) {
    return { portsToAdd: [], portsToRemove: [] };
  }

  if (isSelected) {
    // Adding category - find ports not already active
    const portsToAdd = category.ports.filter(port => !currentPorts.has(port));
    return { portsToAdd, portsToRemove: [] };
  } else {
    // Removing category - remove all category ports
    const portsToRemove = category.ports.filter(port => currentPorts.has(port));
    return { portsToAdd: [], portsToRemove };
  }
}

/**
 * Gets category by ID.
 */
export function getCategoryById(id: string): ExposureCategory | undefined {
  return EXPOSURE_CATEGORIES.find(c => c.id === id);
}

/**
 * Gets recommended categories (for initial setup or suggestions).
 */
export function getRecommendedCategories(): ExposureCategory[] {
  return EXPOSURE_CATEGORIES.filter(c => c.recommended);
}

/**
 * Severity order for sorting categories.
 * Critical risks should appear first, followed by high, then medium.
 */
const SEVERITY_ORDER: Record<ExposureCategoryRisk, number> = {
  critical: 0,
  high: 1,
  medium: 2,
};

/**
 * Gets all categories sorted by severity (critical first, then high, then medium).
 * This provides a consistent, priority-based ordering for display.
 *
 * @returns Categories sorted by risk severity
 */
export function getCategoriesBySeverity(): ExposureCategory[] {
  return [...EXPOSURE_CATEGORIES].sort(
    (a, b) => SEVERITY_ORDER[a.risk] - SEVERITY_ORDER[b.risk]
  );
}
